import { useState } from 'react';

import { useUnit } from 'effector-react/ssr';

import { Product } from '@domains/product';

import {
    $isCartInitialized,
    $isCartLoading,
    addProductsToCartFx,
    setQuantityProductFx,
    useFindCartItemByProductId,
} from '@stores/cart';

import { isValueSet } from '@utils/guards';
import { SnackBar } from '@utils/snackbar';

export const useProduct = (productId: Product['id']) => {
    const [isAddToCartPending, setIsAddToCartPending] = useState<boolean>(false);
    const [isCartLoading, isCartInitialized] = useUnit([$isCartLoading, $isCartInitialized]);
    const { addProductsToCart, setQuantityProduct } = useUnit({
        addProductsToCart: addProductsToCartFx,
        setQuantityProduct: setQuantityProductFx,
    });
    const cartItem = useFindCartItemByProductId(productId);

    const isDisabledCartInterface = isCartLoading || !isCartInitialized;

    const handleAddProductToCart = async (isPrevented = false): Promise<void> => {
        if (isPrevented) return;

        try {
            setIsAddToCartPending(true);
            await addProductsToCart({ products: [{ product_id: productId, quantity: 1 }] });
        } catch (error) {
            SnackBar.handleApiError(error);
        } finally {
            setIsAddToCartPending(false);
        }
    };

    const handleDebouncedQuantityChange = async (
        quantity: number,
        rollBack?: VoidFunction,
    ): Promise<void> => {
        if (isDisabledCartInterface || !isValueSet(cartItem)) {
            if (isValueSet(rollBack)) rollBack();
            return;
        }

        try {
            await setQuantityProduct({
                quantity: quantity,
                item_id: cartItem.id,
            });
        } catch (error) {
            SnackBar.handleApiError(error);
            if (isValueSet(rollBack)) rollBack();
        }
    };

    const handleRemoveFromCart = async (): Promise<void> => {
        await handleDebouncedQuantityChange(0, undefined);
    };

    return {
        isCartInitialized,
        isDisabledCartInterface,
        isAddToCartPending,
        cartItem,
        handleAddProductToCart,
        handleDebouncedQuantityChange,
        handleRemoveFromCart,
    };
};
